import { useState, type FC } from "react";
import { useNavigate } from "react-router-dom";
import { FormattedMessage } from "react-intl";
import { GKLoading, GKLoadingType } from "@granular/gds";
import { LoginCardLayout } from "../../LoginCardLayout";
import { getAuthEnv, makeAuthApp } from "../../../services/auth/new-msal/init";
import {
  B_TO_C_POLICIES,
  getAuthConfig,
  type AuthSource,
} from "@granular/fabric3-definitions";
import { useMount } from "../../..";

/**
 * Attempts to automatically log in a user after they complete the invitation signup flow.
 * Uses silent SSO to leverage the active Azure B2C session created during signup.
 * Always assumes B2C flow (grower accounts) since B2B users don't go through invite signup.
 * Falls back to manual login selection if silent authentication fails.
 */
export const AutoLoginAfterInvite: FC = () => {
  const navigate = useNavigate();
  const [attemptingLogin, setAttemptingLogin] = useState(true);

  useMount(() => {
    void (async () => {
      try {
        // Always use B2C flow for invite signups (growers)
        // B2B (Corteva employees) don't go through the invite signup flow
        const flow: AuthSource = "b2c";

        console.log(
          `[MSAL] Attempting silent authentication for ${flow} after invite signup`,
        );
        console.debug("[MSAL] Current URL:", globalThis.location.href);
        console.debug("[MSAL] Search params:", globalThis.location.search);
        console.debug("[MSAL] Hash:", globalThis.location.hash);

        // Create auth app for the specified flow with the REDEEM policy.
        // Must use the SAME policy that was used for signup
        const authConfig = getAuthConfig(
          getAuthEnv(),
          flow,
          B_TO_C_POLICIES.REDEEM,
        );

        // CRITICAL: Create auth app BEFORE calling handleRedirectPromise
        // The first instance to call handleRedirectPromise will consume the code
        const authApp = await makeAuthApp(authConfig);

        // Get scopes for this flow
        const appScope = `${authConfig.appId}/internal`;
        const scopes = [appScope, "offline_access", "openid", "profile"];

        // If this has already been handled elsewhere (e.g., by global init),
        // handleRedirectPromise will return null, but the active account should already be set
        console.log("[MSAL] Checking for redirect response...");
        const redirectResponse = await authApp.handleRedirectPromise();
        console.debug("[MSAL] Redirect response:", redirectResponse);

        let account = redirectResponse?.account ?? undefined;

        if (account) {
          console.log(
            "[MSAL] ✅ Got account from redirect response, setting as active",
          );
          authApp.setActiveAccount(account);
        } else {
          // No redirect response - check if we already have an active account
          account = authApp.getActiveAccount() ?? undefined;
          console.log("[MSAL] Active account from cache:", account?.username);
        }

        if (account) {
          console.log("[MSAL] ✅ Account found, logging in automatically");

          // Redirect to app root - auth system will initialize with this account
          navigate("/?spaSSO=true", { replace: true });
          return;
        }

        // Last resort: Redirect to Azure B2C with standard signin policy
        // The user just created an account, so Azure B2C should recognize them
        console.log("[MSAL] No cached account, initiating login redirect...");

        // Create a new auth app with the STANDARD B2C_1A_GRANULAR_SIGNIN policy
        const standardAuthConfig = getAuthConfig(
          getAuthEnv(),
          flow,
          B_TO_C_POLICIES.SIGN_IN,
        );
        const standardAuthApp = await makeAuthApp(standardAuthConfig);

        // Redirect to Azure B2C signin
        // Azure B2C should recognize the user since they just signed up
        console.log(
          "[MSAL] Redirecting to Azure B2C signin (user just created account)...",
        );
        await standardAuthApp.loginRedirect({
          scopes,
          redirectUri: `${globalThis.location.origin}/`,
        });

        // loginRedirect never returns - it redirects the browser
      } catch (error) {
        console.error("[MSAL] Error during auto-login after invite:", error);
        // On error, fall back to manual login
        navigate("/login/identify?fromInvite=true");
      } finally {
        setAttemptingLogin(false);
      }
    })();
  });

  return (
    <LoginCardLayout>
      <GKLoading
        className="pr-1"
        isLoading={attemptingLogin}
        type={GKLoadingType.Spinner}
        spinnerProps={{ width: 32 }}
      />
      <p className="mt-4">
        <FormattedMessage id="completing_sign_up" />
        ...
      </p>
    </LoginCardLayout>
  );
};
